import {
  DEFAULT_ALL_DEVICES_TAB_COLUMNS,
  DEFAULT_ECHO_TAB_COLUMNS,
  DEFAULT_PIVOT_TAB_COLUMNS,
  DEVICES_PAGE_TAB
} from 'common/utils/constants';
import {
  getDeviceAzimuthsAndDowntilts,
  getDeviceGains,
  connectionStatusColAccessor,
  swVersionColAccessor,
  genModelColAccessor,
  wifiColAccessor,
  deviceTypeColAccessor,
  lockedUnlockedColAccessor,
  modelColAccessor,
  genColAccessor,
  alarmsColAccessor,
  deviceIdColAccessor,
  locationColAccessor
} from '../DeviceTable/utils';
import { isItBundle } from 'common/utils/deviceTwinUtils';
import { getDonorUnitFromBundleMembers, getServiceUnits } from 'common/utils/helpers';

export const EXPORT_OPTIONS = {
  EXPORT_ALL: 'Export All Columns',
  EXPORT_SELECTED: 'Export Selected Columns'
};

export const TOAST_MESSAGES = {
  pending: 'Downloading CSV file...',
  success: 'Download completed.',
  failed: 'Download failed,'
};

export const getDefaultTabColumns = (activeTab) => {
  return activeTab === DEVICES_PAGE_TAB.pivot
    ? DEFAULT_PIVOT_TAB_COLUMNS
    : activeTab === DEVICES_PAGE_TAB.echo
    ? DEFAULT_ECHO_TAB_COLUMNS
    : DEFAULT_ALL_DEVICES_TAB_COLUMNS;
};

export const generateExportableTableDataForBundleMembers = (bundle, enabledColumns) => {
  const members = bundle?.members || [];
  if (members.length === 0) return [];

  const reorderedMembers = [];
  const donorUnit = getDonorUnitFromBundleMembers(members);
  if (donorUnit) reorderedMembers.push(donorUnit);

  const serviceUnits = getServiceUnits(members) || [];
  reorderedMembers.push(...serviceUnits);

  const memberRows = reorderedMembers.map((member) => {
    const obj = {};

    enabledColumns.forEach((col) => {
      const accessor = deviceTwinDataAccessors?.[col.id]?.accessor;
      if (accessor) {
        const isBundleColumn = ['pod_name_col', 'pod_code_col', 'gen_col'].includes(col.id);
        const value = accessor(isBundleColumn ? bundle : member);
        if (col.id === 'location_col') {
          const latLong = value.split(', ');
          obj['Latitude'] = latLong[0];
          obj['Longitude'] = latLong[1];
        } else {
          obj[col.uiName] = value;
        }
      }
    });

    return obj;
  });

  return memberRows;
};

export const generateExportableTableData = (data, activeTab, exportType, selectedColumns) => {
  if (!data || data.length === 0) return {};

  const isExportAll = exportType === EXPORT_OPTIONS.EXPORT_ALL;
  const tabColumns = isExportAll ? getDefaultTabColumns(activeTab) : selectedColumns;

  const enabledColumns = tabColumns.filter(
    (col) =>
      (isExportAll ? col.disabled === false : col.disabled === false && col.selected === true) &&
      col.id !== 'protected_col' &&
      col.id !== 'factory_col'
  );

  // ensure to add deviceId column when activeTab is pivot
  if (activeTab === DEVICES_PAGE_TAB.pivot && !enabledColumns.find((col) => col.id === 'device_id_col')) {
    const deviceIdCol = DEFAULT_PIVOT_TAB_COLUMNS.find((col) => col.id === 'device_id_col');
    enabledColumns.push(deviceIdCol);
  }

  const columnArr = enabledColumns.flatMap((col) => {
    if (col.id === 'location_col') {
      return [{ name: 'Latitude' }, { name: 'Longitude' }];
    }
    return { name: col.uiName };
  });

  const rowArr = data.reduce((accumulator, device) => {
    if (isItBundle(device)) {
      const memberRowArr = generateExportableTableDataForBundleMembers(device, enabledColumns);
      return memberRowArr.length ? [...accumulator, ...memberRowArr] : accumulator;
    }

    const obj = {};
    enabledColumns.forEach((col) => {
      const accessor = deviceTwinDataAccessors?.[col.id]?.accessor;
      if (accessor) {
        const value = accessor(device);
        if (col.id === 'location_col') {
          const latLong = value.split(', ');
          obj['Latitude'] = latLong[0] || 'N/A';
          obj['Longitude'] = latLong[1] || 'N/A';
        } else {
          obj[col.uiName] = value;
        }
      }
    });

    return [...accumulator, obj];
  }, []);

  return { columns: columnArr, rows: rowArr };
};

export const modifyFilters = (activeTab, filterDictionary, activePolygon) => {
  const newFilterDictionary = JSON.parse(JSON.stringify(filterDictionary));

  if (activeTab === DEVICES_PAGE_TAB.pivot) {
    newFilterDictionary.hasBundleId = true;
  } else {
    delete newFilterDictionary['hasBundleId'];
  }

  if (activePolygon) {
    const {
      geometry: { coordinates }
    } = activePolygon;
    newFilterDictionary.polygon = coordinates;
  } else {
    delete newFilterDictionary.polygon;
  }

  Object.keys(newFilterDictionary).forEach((filterName) => {
    if (filterName === 'properties.reported.productType') {
      if (
        newFilterDictionary['properties.reported.productType'] &&
        newFilterDictionary['properties.reported.productType'].length > 0
      ) {
        newFilterDictionary.productType = newFilterDictionary['properties.reported.productType'];
      }

      delete newFilterDictionary['properties.reported.productType'];
    } else if (filterName === 'properties.reported.operationState') {
      newFilterDictionary.operationState = newFilterDictionary['properties.reported.operationState'];
      delete newFilterDictionary['properties.reported.operationState'];
    } else if (filterName === 'properties.reported.adminState') {
      newFilterDictionary.adminState = newFilterDictionary['properties.reported.adminState'];
      delete newFilterDictionary['properties.reported.adminState'];
    } else if (filterName === 'provisionTime') {
      newFilterDictionary.daterange = { field: 'tags.provisionTime', ...newFilterDictionary.provisionTime };
      delete newFilterDictionary.provisionTime;
    } else if (filterName.startsWith('tags.fm-alarms')) {
      (newFilterDictionary.alarmState = newFilterDictionary.alarmState || []).push(filterName.split('.')[2]);
      delete newFilterDictionary[filterName];
    }
  });
  return newFilterDictionary;
};

export const deviceTwinDataAccessors = {
  pod_name_col: {
    id: 'pod_name_col',
    uiName: 'Pod Name',
    accessor: (bundle) => bundle?.name || 'Unknown'
  },
  pod_code_col: {
    id: 'pod_code_col',
    uiName: 'Pod Code',
    accessor: (bundle) => bundle?.code || 'POD_Unknown'
  },
  gen_col: {
    id: 'gen_col',
    uiName: 'Gen',
    accessor: (bundle) => genColAccessor(bundle)
  },
  alarms_col: {
    id: 'alarms_col',
    uiName: 'Alarms',
    accessor: (device) => alarmsColAccessor(device)
  },
  connection_status_col: {
    id: 'connection_status_col',
    uiName: 'Connection Status',
    accessor: (device) => connectionStatusColAccessor(device)?.connectionStatus || 'Unknown'
  },
  sw_version_col: {
    id: 'sw_version_col',
    uiName: 'SW Version',
    accessor: (device) => swVersionColAccessor(device)
  },
  device_id_col: {
    id: 'device_id_col',
    uiName: 'Device ID',
    accessor: (device) => deviceIdColAccessor(device)
  },
  dl_gain_v_col: {
    id: 'dl_gain_v_col',
    uiName: 'DL Gain-V',
    accessor: (device) => getDeviceGains(device).dlv
  },
  dl_gain_h_col: {
    id: 'dl_gain_h_col',
    uiName: 'DL Gain-H',
    accessor: (device) => getDeviceGains(device).dlh
  },
  ul_gain_v_col: {
    id: 'ul_gain_v_col',
    uiName: 'UL Gain-V',
    accessor: (device) => getDeviceGains(device).ulv
  },
  ul_gain_h_col: {
    id: 'ul_gain_h_col',
    uiName: 'UL Gain-H',
    accessor: (device) => getDeviceGains(device).ulh
  },
  locked_unlocked_col: {
    id: 'locked_unlocked_col',
    uiName: 'Locked/Unlocked',
    accessor: (device) => lockedUnlockedColAccessor(device)
  },
  wifi_enabled_col: {
    id: 'wifi_enabled_col',
    uiName: 'Wi-Fi Enabled',
    accessor: (device) => wifiColAccessor(device)
  },
  azimuth_electrical_col: {
    id: 'azimuth_electrical_col',
    uiName: 'Azimuth - Electrical',
    accessor: (device) => getDeviceAzimuthsAndDowntilts(device)?.electricalAz
  },
  downtilt_electrical_col: {
    id: 'downtilt_electrical_col',
    uiName: 'Downtilt - Electrical',
    accessor: (device) => getDeviceAzimuthsAndDowntilts(device)?.electricalDowntilt
  },
  echo_id_col: {
    id: 'echo_id_col',
    uiName: 'Echo ID',
    accessor: (device) => deviceIdColAccessor(device)
  },
  echo_name_col: {
    id: 'echo_name_col',
    uiName: 'Echo Name',
    accessor: (device) => device?.tags?.userLabel || 'Unknown'
  },
  model_col: {
    id: 'model_col',
    uiName: 'Model',
    accessor: (device) => modelColAccessor(device)
  },
  device_type_col: {
    id: 'device_type_col',
    uiName: 'Device Type',
    accessor: (device) => deviceTypeColAccessor(device)
  },
  gen_model_col: {
    id: 'gen_model_col',
    uiName: 'Gen/Model',
    accessor: (device) => genModelColAccessor(device)
  },
  location_col: {
    id: 'location_col',
    uiName: 'Location',
    accessor: (device) => locationColAccessor(device)
  }
};
